import { Container, injectable } from 'inversify';

import type {
  Inversify_Dependencies,
  Inversify_DynamicKeys,
  Inversify_Injector_Value,
  Inversify_InstanceKeys,
  Inversify_Importer,
  Inversify_Settings,
  Inversify_Value,
  Inversify_Switch,
  Newable
} from './Inversify.types';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type ClassType = abstract new (...args: any) => unknown;

/**
 * The `Inversify` class is a utility for managing dependency injection using the InversifyJS library.
 * It provides a centralized container for binding and resolving dependencies, supporting both static
 * and dynamic module imports. This class is designed to work with a set of predefined dependencies
 * and offers features like mocking and dynamic module resolution.
 *
 * @template D - A type extending `Inversify_Dependencies`, representing the structure of the dependencies.
 */
export class Inversify<D extends Inversify_Dependencies> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private static _instance: Inversify<Record<string, any>> | null;
  private static inyectablesList: ClassType[] = [];
  /** The Inversify container instance that holds the registered dependencies. */
  readonly container: Container;

  private constructor(
    private readonly dependencies: D,
    readonly settings: Inversify_Settings<D> = {},
    private readonly symDict: Record<keyof D, symbol>
  ) {
    this.container = new Container();
  }

  /**
   * Retrieves the list of injectable instance dependencies managed by the Inversify container.
   *
   * @returns An array containing the injectable dependencies.
   */
  static get list() {
    return Inversify.inyectablesList;
  }

  // TODO: https://github.com/microsoft/TypeScript/issues/47679

  /**
   * Decorator factory to mark a class as injectable and register it with a specific scope.
   *
   * @param [scope='dynamic'] - The injection scope for the class. Can be either:
   *   - `'dynamic'`: _(default)_ Indicates that a new instance of the class will be created each
   *     time it is injected.
   *   - `'instance'`: Indicates that a single instance of the class will be shared.
   * @returns A decorator function that registers the target class as injectable.
   *
   * @remarks
   * Applies the `@injectable` decorator from the Inversify library to the target class.
   * Also, make sure that the class is exported as the default export of the module.
   *
   * @example
   * ```ts
   * \@Inversify.injectable('instance')
   * export default class MyService {
   *   // Service implementation
   * }
   * ```
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  static injectable(scope: 'instance' | 'dynamic' = 'dynamic'): (target: ClassType) => void {
    return (target: ClassType) => {
      this.inyectablesList.push(target);
      injectable()(target);
    };
  }

  /**
   * Creates a singleton instance of the `Inversify` class with the provided dependencies and settings.
   * If an instance already exists, it returns the existing instance.
   *
   * @template D - A type that extends `Inversify_Dependencies`, representing the dependencies to be injected.
   * @param dependencies - An object containing the dependencies to be managed by the `Inversify` container.
   * @param settings - Optional settings for configuring the `Inversify` instance.
   * @returns The singleton instance of the `Inversify` class.
   */
  static create<D extends Inversify_Dependencies>(
    dependencies: D,
    settings?: Inversify_Settings<D>
  ): Inversify<D> {
    if (Inversify._instance) return Inversify._instance as Inversify<D>;

    const symDict = Object.keys(dependencies).reduce(
      (acc, key) => {
        (acc as Record<string, symbol>)[key] = Symbol(key);
        return acc;
      },
      {} as Record<keyof D, symbol>
    );

    const instance = (Inversify._instance = new Inversify<D>(dependencies, settings, symDict));

    Object.entries(dependencies).forEach(([key, dependency]) => {
      instance._bind(key, dependency);
    });

    return instance;
  }

  /**
   * Retrieves an instance from the Inversify container based on the provided key.
   *
   * @template T - A key that extends the `Inversify_InstanceKeys<D>` type, representing
   *               the valid keys for dependency injection.
   * @param key - The key used to identify the dependency in the container.
   * @returns The resolved value of the dependency, typed as `Inversify_Injector_Value<D[T]>`.
   */
  get<T extends Inversify_InstanceKeys<D>>(key: T): Inversify_Injector_Value<D[T]> {
    return this.container.get(this.symDict[key]);
  }

  /**
   * Gathers a dynamic dependency from the Inversify container based on the provided key.
   *
   * @template T - A key that extends the `Inversify_DynamicKeys<D>` type, representing
   *               the valid keys for dynamic dependencies.
   * @param key - The key used to identify the dynamic dependency in the container.
   * @returns A promise that resolves to the value of the dynamic dependency,
   *          typed as `Inversify_Injector_Value<D[T]>`.
   */
  gather<T extends Inversify_DynamicKeys<D>>(key: T): Inversify_Injector_Value<D[T]> {
    return this.container.get<CallableFunction | null>(this.symDict[key])?.();
  }

  private _bind(key: keyof D, dependency: Inversify_Value): void {
    const mock = this.settings.mockAll || ('mock' in dependency && dependency.mock) || false;
    const sym = this.symDict[key];
    const instanceGatherer =
      'dynamicImport' in dependency
        ? dependency.dynamicImport
        : 'mockable' in dependency
          ? dependency.mockable
          : dependency.newable;

    if (isClass(instanceGatherer)) {
      this.container.bind(sym).to(instanceGatherer);
      return;
    }

    this._bindDynamicModule(sym, instanceGatherer, mock);
  }

  private _bindDynamicModule(
    sym: symbol,
    dynamicImport: Inversify_Importer | Inversify_Switch,
    mock: boolean
  ): void {
    const resolvedIdentifier = `${sym.description}_resolved`;

    this.container.bind(sym).toDynamicValue(() => async () => {
      const provider = dynamicImport(mock);

      const dependency =
        provider instanceof Promise
          ? await provider.then((resolvedModule) => resolvedModule.default)
          : provider;

      if (!this.container.isBound(resolvedIdentifier))
        this.container.bind(resolvedIdentifier).to(dependency);

      return this.container.get(resolvedIdentifier);
    });
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function isClass<T>(value: any): value is Newable<T> {
  return typeof value === 'function' && /^\s*class\s+/.test(value.toString());
}
